import { useControllerAppModel } from '@/controller/models/ControllerAppModel';
import { ControllerUserModel } from '@/controller/models/ControllerUserModel';
import { ClassNames } from '@emotion/react';
import {
  absolute,
  Card,
  Dialog,
  FlexColumn,
  FlexRow,
  IconButton,
  Intent,
  relative,
  ScrollArea,
} from '@toilet-paper-games/ui';
import { Crown, UserRoundX } from 'lucide-react';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';

export interface PlayersDialogProps {}

// UserCard Component
interface UserCardProps {
  user: ControllerUserModel;
}

const UserCard = observer(({ user }: UserCardProps) => {
  const appStore = useControllerAppModel();

  const isHost = !!appStore.user?.isHost;

  const isUserHost = user.isHost;

  return (
    <Card inset>
      <FlexRow alignItems="center" justifyContent="space-between" gap={10}>
        <div>{user.screenName}</div>
        <FlexRow gap={5}>
          <IconButton
            disabled={!isHost}
            icon={
              <Crown
                css={{
                  rotate: isUserHost ? '45deg' : '0deg',
                  transition: 'rotate 0.2s',
                }}
              />
            }
            intent={isUserHost ? Intent.Warning : Intent.Success}
            onClick={() => {
              if (isUserHost) {
                appStore.demoteUser(user.id);
              } else {
                appStore.promoteUser(user.id);
              }
            }}
          />
          <IconButton
            disabled={!isHost}
            icon={<UserRoundX />}
            intent={Intent.Danger}
            onClick={() => {
              appStore.kickUser(user.id);
            }}
          />
        </FlexRow>
      </FlexRow>
    </Card>
  );
});

// UserList Component
const UserList = observer(() => {
  const appStore = useControllerAppModel();
  const connectedUsers = appStore.connectedUsers;

  return (
    <FlexColumn gap={10}>
      {connectedUsers.map((user) => (
        <UserCard key={user.id} user={user} />
      ))}
    </FlexColumn>
  );
});

// PlayersDialog Component
export const PlayersDialog = observer((props: PlayersDialogProps) => {
  const appStore = useControllerAppModel();
  const isOpen = appStore.uiModel.showPlayersDialog;

  return (
    <ClassNames>
      {({ css }) => (
        <Dialog
          open={isOpen}
          title="Players"
          onOpenChange={action((open) => (appStore.uiModel.showPlayersDialog = open))}
          cardProps={{
            className: css([
              {
                width: 'min(calc(100vw - 20px), 300px)',
                height: 'min(calc(100vh - 20px), 500px)',
                padding: 0,
                overflow: 'hidden',
              },
              relative(),
            ]),
          }}
        >
          <ScrollArea css={[absolute(0, 0, 0, 0), { padding: 10 }]}>
            <UserList />
          </ScrollArea>
        </Dialog>
      )}
    </ClassNames>
  );
});
